import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Switch } from "../../components/ui/switch";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { methodologyService } from "../../lib/firebase";
import { toast } from "../../components/ui/use-toast";
import { Terminal, ExternalLink, CheckCircle, XCircle, AlertCircle } from "lucide-react";

const ADMIN_EMAIL = "admin@rawpa.com";

export const AdminGTFOBinsPage = (): JSX.Element => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [gtfobinsEnabled, setGtfobinsEnabled] = useState(false);
  const [customDescription, setCustomDescription] = useState("");
  const [apiStatus, setApiStatus] = useState<'checking' | 'online' | 'offline'>('checking');
  const [sampleData, setSampleData] = useState<any[]>([]);

  const auth = getAuth();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user && user.email === ADMIN_EMAIL) {
        setIsAdmin(true);
        await loadGTFOBinsSettings();
        await checkApiStatus();
        await loadSampleData();
      } else {
        setIsAdmin(false);
        navigate('/aAyuiOcvMq');
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [auth, navigate]);

  const loadGTFOBinsSettings = async () => {
    try {
      const settings = await methodologyService.getSystemSettings();
      setGtfobinsEnabled(!settings.disableGTFOBins);
      setCustomDescription(settings.gtfobinsDescription || "Unix binaries for bypassing local security restrictions");
    } catch (error) {
      console.error("Error loading GTFOBins settings:", error);
    }
  };

  const checkApiStatus = async () => {
    setApiStatus('checking');
    try {
      const response = await fetch('https://gtfobins.github.io/');
      setApiStatus(response.ok ? 'online' : 'offline');
    } catch (error) {
      setApiStatus('offline');
    }
  };

  const loadSampleData = async () => {
    try {
      // Load sample data from the local JSON file
      const response = await fetch('/gtfobins.json');
      if (response.ok) {
        const data = await response.json();
        const sampleData = data.slice(0, 5).map(binary => ({
          name: binary.name,
          functions: binary.functions.map(f => ({ name: f.name }))
        }));
        setSampleData(sampleData);
      } else {
        // Fallback sample data
        const fallbackData = [
          { name: "7z", functions: [{ name: "File read" }, { name: "Sudo" }] },
          { name: "awk", functions: [{ name: "Shell" }, { name: "File read" }] },
          { name: "bash", functions: [{ name: "Shell" }, { name: "File read" }] },
          { name: "cat", functions: [{ name: "File read" }] },
          { name: "curl", functions: [{ name: "File download" }, { name: "File read" }] }
        ];
        setSampleData(fallbackData);
      }
    } catch (error) {
      console.error("Error loading sample data:", error);
      // Fallback sample data
      const fallbackData = [
        { name: "7z", functions: [{ name: "File read" }, { name: "Sudo" }] },
        { name: "awk", functions: [{ name: "Shell" }, { name: "File read" }] },
        { name: "bash", functions: [{ name: "Shell" }, { name: "File read" }] },
        { name: "cat", functions: [{ name: "File read" }] },
        { name: "curl", functions: [{ name: "File download" }, { name: "File read" }] }
      ];
      setSampleData(fallbackData);
    }
  };

  const getStats = () => {
    return { 
      binaries: 390, 
      functions: 1460, 
      types: [
        'shell', 'sudo', 'suid', 'file-read', 'file-write', 'file-download', 
        'file-upload', 'reverse-shell', 'bind-shell', 'capabilities', 
        'limited-suid', 'library-load', 'command'
      ] 
    };
  };

  const handleToggleGTFOBins = async (enabled: boolean) => {
    try {
      await methodologyService.updateSystemSettings({
        disableGTFOBins: !enabled
      });
      setGtfobinsEnabled(enabled);
      toast({
        title: "GTFOBins Updated",
        description: `GTFOBins has been ${enabled ? 'enabled' : 'disabled'} for users.`,
      });
    } catch (error) {
      console.error("Error updating GTFOBins settings:", error);
      toast({
        title: "Error",
        description: "Failed to update GTFOBins settings.",
        variant: "destructive",
      });
    }
  };

  const handleSaveDescription = async () => {
    try {
      await methodologyService.updateSystemSettings({
        gtfobinsDescription: customDescription
      });
      toast({
        title: "Description Saved",
        description: "GTFOBins description has been updated.",
      });
    } catch (error) {
      console.error("Error saving description:", error);
      toast({
        title: "Error",
        description: "Failed to save description.",
        variant: "destructive",
      });
    }
  };

  const handleSignOut = async () => {
    try {
      await auth.signOut();
      navigate('/aAyuiOcvMq');
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#e1dcdc75] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#daed6e] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#e1dcdc75] p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <Button
              variant="outline"
              onClick={() => navigate("/aAyuiOcvMq/toolkit")}
              className="mb-4"
            >
              ← Back to Toolkit Management
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-purple-500 rounded-lg flex items-center justify-center">
                <Terminal className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-black">GTFOBins Management</h1>
                <p className="text-gray-600">Manage Unix binaries integration</p>
              </div>
            </div>
          </div>
          <Button onClick={handleSignOut} variant="outline">
            Sign Out
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Settings Panel */}
          <div className="space-y-6">
            <Card className="border-2 border-[#E7E7E7]">
              <CardHeader>
                <CardTitle className="text-xl text-black">GTFOBins Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Enable/Disable Toggle */}
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="gtfobins-toggle" className="text-base font-medium">
                      Enable GTFOBins for Users
                    </Label>
                    <p className="text-sm text-gray-600 mt-1">
                      Allow users to access GTFOBins data
                    </p>
                  </div>
                  <Switch
                    id="gtfobins-toggle"
                    checked={gtfobinsEnabled}
                    onCheckedChange={handleToggleGTFOBins}
                  />
                </div>

                {/* Custom Description */}
                <div className="space-y-3">
                  <Label htmlFor="description" className="text-base font-medium">
                    Custom Description
                  </Label>
                  <Textarea
                    id="description"
                    placeholder="Enter a custom description for GTFOBins..."
                    value={customDescription}
                    onChange={(e) => setCustomDescription(e.target.value)}
                    className="min-h-[100px]"
                  />
                  <Button onClick={handleSaveDescription} className="w-full">
                    Save Description
                  </Button>
                </div>

                {/* API Status */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium">API Status:</span>
                    {apiStatus === 'checking' && (
                      <div className="flex items-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                        <span className="text-sm text-gray-600">Checking...</span>
                      </div>
                    )}
                    {apiStatus === 'online' && (
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span className="text-sm text-green-600">Online</span>
                      </div>
                    )}
                    {apiStatus === 'offline' && (
                      <div className="flex items-center gap-2">
                        <XCircle className="w-4 h-4 text-red-600" />
                        <span className="text-sm text-red-600">Offline</span>
                      </div>
                    )}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={checkApiStatus}
                  >
                    Refresh
                  </Button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-blue-800">Total Binaries</h3>
                    <p className="text-2xl font-bold text-blue-600">{getStats().binaries}</p>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-green-800">Total Functions</h3>
                    <p className="text-2xl font-bold text-green-600">{getStats().functions}</p>
                  </div>
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-purple-800">Function Types</h3>
                    <p className="text-2xl font-bold text-purple-600">{getStats().types.length}</p>
                  </div>
                </div>

                <div className="mb-6">
                  <h3 className="text-lg font-semibold mb-2">Function Types Available</h3>
                  <div className="flex flex-wrap gap-2">
                    {getStats().types.map((type) => (
                      <Badge key={type} variant="secondary" className="text-xs">
                        {type}
                      </Badge>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* License Information */}
            <Card className="border-2 border-[#E7E7E7]">
              <CardHeader>
                <CardTitle className="text-xl text-black">License Information</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <p className="text-sm text-gray-600">
                    GTFOBins is licensed under the <strong>GPL-3.0</strong> license.
                  </p>
                  <p className="text-sm text-gray-500">
                    Note: This integration provides sample data for demonstration. For the complete and up-to-date GTFOBins database, visit the official website.
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open('https://gtfobins.github.io/', '_blank')}
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Visit GTFOBins
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open('https://github.com/GTFOBins', '_blank')}
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      GitHub Repository
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Preview Panel */}
          <div className="space-y-6">
            <Card className="border-2 border-[#E7E7E7]">
              <CardHeader>
                <CardTitle className="text-xl text-black">User Preview</CardTitle>
                <p className="text-sm text-gray-600">
                  How GTFOBins will appear to users
                </p>
              </CardHeader>
              <CardContent>
                {gtfobinsEnabled ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 p-4 bg-purple-50 rounded-lg border border-purple-200">
                      <Terminal className="w-6 h-6 text-purple-600" />
                      <div>
                        <h3 className="font-semibold text-purple-900">GTFOBins</h3>
                        <p className="text-sm text-purple-700">{customDescription}</p>
                      </div>
                      <Badge variant="secondary" className="ml-auto">Available</Badge>
                    </div>
                    
                    {sampleData.length > 0 && (
                      <div className="space-y-2">
                        <h4 className="font-medium text-gray-900">Sample Binaries:</h4>
                        {sampleData.map((binary, index) => (
                          <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                            <span className="text-sm font-mono">{binary.name}</span>
                            <Badge variant="outline" className="text-xs">
                              {binary.functions?.length || 0} functions
                            </Badge>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                    <AlertCircle className="w-6 h-6 text-gray-400" />
                    <div>
                      <h3 className="font-semibold text-gray-900">GTFOBins</h3>
                      <p className="text-sm text-gray-600">Currently disabled for users</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}; 